import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Wallet, Store, ShoppingCart, Package, Calendar, Download, Plus } from "lucide-react";
import DataTable from "react-data-table-component";

const MerchantDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get merchant data from location state or use default
  const merchantData = location.state?.merchant || {
    merchantName: "Merchant Store Name",
    storeCode: "STR-001",
    facility: "NBP",
    location: "MAX-GATE-1",
    pdlNumber: "1234567",
    pdlName: "Juan Dela Cruz",
    status: "active"
  };

  const [isActive, setIsActive] = useState(merchantData.status === "active");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Market");
  const [orderFilter, setOrderFilter] = useState("All");

  // Sample data
  const orderHistory = [
    { id: 1, orderNumber: "OS-2025-01", date: "12/15/2025, 09:30 AM", status: "Submitted" },
    { id: 2, orderNumber: "OS-2025-02", date: "12/15/2025, 09:30 AM", status: "Approved" },
    { id: 3, orderNumber: "OS-2025-03", date: "12/15/2025, 09:30 AM", status: "Approved" }
  ];

  const performanceData = [
    { name: "Shampoo", value: 65, color: "#ef4444" },
    { name: "Coffee", value: 75, color: "#a855f7" },
    { name: "Rice", value: 85, color: "#3b82f6" }
  ];

  const inventoryData = [
    {
      id: 1,
      autoReplenishment: true,
      sku: "SKU-001",
      item: "Shampoo 500ml",
      qty: 1,
      category: "Food",
      cost: "P40.00",
      uom: "Piece",
      srp: "40.00",
      availability: "In Stock"
    },
    {
      id: 2,
      autoReplenishment: true,
      sku: "SKU-002",
      item: "Coffee",
      qty: 4,
      category: "Food",
      cost: "P120.90",
      uom: "Box",
      srp: "P100.00",
      availability: "Low Stock"
    },
    {
      id: 3,
      autoReplenishment: true,
      sku: "SKU-003",
      item: "Rice 25kg",
      qty: 2,
      category: "Food",
      cost: "P120",
      uom: "Box",
      srp: "P120",
      availability: "Out of Stock"
    },
    {
      id: 4,
      autoReplenishment: true,
      sku: "SKU-003",
      item: "Detergent Powder 1kg",
      qty: 3,
      category: "Food",
      cost: "P120",
      uom: "Piece",
      srp: "P120",
      availability: "Out of Stock"
    }
  ];

  const inventoryColumns = [
    {
      name: "Auto-Replenishment",
      cell: row => (
        <label className="merchant-detail-toggle">
          <input
            type="checkbox"
            checked={row.autoReplenishment}
            onChange={() => {}}
            className="merchant-detail-toggle-input"
          />
          <span className="merchant-detail-toggle-slider"></span>
        </label>
      ),
      width: "12%"
    },
    {
      name: "SKU",
      selector: row => row.sku,
      width: "10%"
    },
    {
      name: "Item",
      selector: row => row.item,
      width: "18%"
    },
    {
      name: "QTY",
      selector: row => row.qty,
      width: "8%"
    },
    {
      name: "Category",
      selector: row => row.category,
      width: "10%"
    },
    {
      name: "Cost",
      selector: row => row.cost,
      width: "10%"
    },
    {
      name: "UOM",
      selector: row => row.uom,
      width: "8%"
    },
    {
      name: "SRP",
      selector: row => row.srp,
      width: "10%"
    },
    {
      name: "Availability",
      cell: row => {
        const status = row.availability;
        let badgeClass = "merchant-detail-availability";
        if (status === "In Stock") badgeClass += " in-stock";
        else if (status === "Low Stock") badgeClass += " low-stock";
        else badgeClass += " out-of-stock";
        return <span className={badgeClass}>{status}</span>;
      },
      width: "14%"
    }
  ];

  const customStyles = {
    table: {
      style: {
        tableLayout: 'fixed'
      }
    },
    headRow: {
      style: {
        backgroundColor: "#0F2A40",
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: 600,
        minHeight: "48px"
      }
    },
    headCells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        textTransform: "uppercase",
        textAlign: "center"
      }
    },
    rows: {
      style: {
        minHeight: "56px"
      }
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        paddingTop: "12px",
        paddingBottom: "12px"
      }
    }
  };

  return (
    <div className="dashboard-content">
      <div className="merchant-detail-page">
        {/* Header */}
        <div className="merchant-detail-header">
          <button className="merchant-detail-back-btn" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </button>
          <div className="merchant-detail-header-info">
            <h1 className="merchant-detail-title">
              {merchantData.merchantName} ({merchantData.storeCode})
            </h1>
            <div className="merchant-detail-info-row">
              <span className="merchant-detail-info-text">
                Facility: {merchantData.facility} - Maximum | Location: {merchantData.location}
              </span>
            </div>
            <div className="merchant-detail-info-row">
              <span className="merchant-detail-info-text">
                PDL Info: {merchantData.pdlNumber} - {merchantData.pdlName}
              </span>
            </div>
          </div>
          <div className="merchant-detail-header-actions">
            <div className="merchant-detail-status-toggle">
              <label className="merchant-detail-toggle-label">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="merchant-detail-status-toggle-input"
                />
                <span className="merchant-detail-status-toggle-slider"></span>
                <span className="merchant-detail-status-label-text">Active</span>
              </label>
            </div>
            <div className="merchant-detail-action-buttons">
              <button className="merchant-detail-markup-btn">Markup Settings</button>
              <button className="merchant-detail-password-btn">Change Password</button>
              <button className="merchant-detail-export-btn">
                <Download size={18} />
                Export as PDF
              </button>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="merchant-detail-metrics">
          <div className="merchant-detail-metric-card">
            <div className="merchant-detail-metric-content">
              <div className="merchant-detail-metric-info">
                <span className="merchant-detail-metric-label">Wallet Balance</span>
                <span className="merchant-detail-metric-value">PHP40,689.00</span>
              </div>
              <Wallet size={32} className="merchant-detail-metric-icon wallet" />
            </div>
          </div>
          <div className="merchant-detail-metric-card">
            <div className="merchant-detail-metric-content">
              <div className="merchant-detail-metric-info">
                <span className="merchant-detail-metric-label">Total Sales</span>
                <span className="merchant-detail-metric-value">400</span>
              </div>
              <Store size={32} className="merchant-detail-metric-icon sales" />
            </div>
          </div>
          <div className="merchant-detail-metric-card">
            <div className="merchant-detail-metric-content">
              <div className="merchant-detail-metric-info">
                <span className="merchant-detail-metric-label">Completed Orders</span>
                <span className="merchant-detail-metric-value">27</span>
              </div>
              <ShoppingCart size={32} className="merchant-detail-metric-icon completed" />
            </div>
          </div>
          <div className="merchant-detail-metric-card">
            <div className="merchant-detail-metric-content">
              <div className="merchant-detail-metric-info">
                <span className="merchant-detail-metric-label">Pending Orders</span>
                <span className="merchant-detail-metric-value">12</span>
              </div>
              <ShoppingCart size={32} className="merchant-detail-metric-icon pending" />
            </div>
          </div>
          <div className="merchant-detail-metric-card">
            <div className="merchant-detail-metric-content">
              <div className="merchant-detail-metric-info">
                <span className="merchant-detail-metric-label">Low Stocks</span>
                <span className="merchant-detail-metric-value">10</span>
              </div>
              <Package size={32} className="merchant-detail-metric-icon low-stock" />
            </div>
          </div>
          <div className="merchant-detail-date-filters">
            <div className="merchant-detail-date-filter">
              <Calendar size={18} className="merchant-detail-date-icon" />
              <input
                type="date"
                className="merchant-detail-date-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="From"
              />
            </div>
            <div className="merchant-detail-date-filter">
              <Calendar size={18} className="merchant-detail-date-icon" />
              <input
                type="date"
                className="merchant-detail-date-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                placeholder="To"
              />
            </div>
          </div>
        </div>

        {/* Order History and Performance */}
        <div className="merchant-detail-sections-row">
          <div className="merchant-detail-section-card">
            <div className="merchant-detail-section-header">
              <h2 className="merchant-detail-section-title">Order History</h2>
              <select
                className="merchant-detail-filter-select"
                value={orderFilter}
                onChange={(e) => setOrderFilter(e.target.value)}
              >
                <option value="All">All</option>
                <option value="Submitted">Submitted</option>
                <option value="Approved">Approved</option>
                <option value="Pending">Pending</option>
              </select>
            </div>
            <div className="merchant-detail-order-list">
              {orderHistory.map((order) => (
                <div key={order.id} className="merchant-detail-order-item">
                  <div className="merchant-detail-order-info">
                    <span className="merchant-detail-order-number">{order.orderNumber}</span>
                    <span className="merchant-detail-order-date">{order.date}</span>
                  </div>
                  <span className={`merchant-detail-order-status ${order.status.toLowerCase()}`}>
                    {order.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="merchant-detail-section-card">
            <h2 className="merchant-detail-section-title">Performance (Top 3 Items)</h2>
            <div className="merchant-detail-performance-chart">
              {performanceData.map((item, index) => (
                <div key={index} className="merchant-detail-performance-item">
                  <span className="merchant-detail-performance-label">{item.name}</span>
                  <div className="merchant-detail-performance-bar-container">
                    <div
                      className="merchant-detail-performance-bar"
                      style={{
                        width: `${item.value}%`,
                        backgroundColor: item.color
                      }}
                    />
                  </div>
                  <span className="merchant-detail-performance-value">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Inventory Section */}
        <div className="merchant-detail-section-card merchant-detail-inventory-section">
          <div className="merchant-detail-inventory-header">
            <div className="merchant-detail-inventory-header-left">
              <h2 className="merchant-detail-section-title">Inventory</h2>
              <select
                className="merchant-detail-category-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="Market">Market</option>
                <option value="Grocery">Grocery</option>
                <option value="Special">Special</option>
              </select>
            </div>
            <div className="merchant-detail-inventory-header-right">
              <button className="merchant-detail-add-item-btn">
                <Plus size={18} />
                Add Item
              </button>
              <button className="merchant-detail-import-btn">
                <Download size={18} />
                Import CSV
              </button>
            </div>
          </div>
          <div className="merchant-detail-table-container">
            <DataTable
              columns={inventoryColumns}
              data={inventoryData}
              customStyles={customStyles}
              striped
              highlightOnHover
              noHeader={false}
              pagination={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MerchantDetail;

